import React from "react";
import { Chat, LanguageCode, Profile, Video } from "../../models";
import { FormControl, FormControlLabel, InputLabel, MenuItem, NativeSelect, Select, SelectChangeEvent, Switch, TextField } from "@mui/material";

interface VideoFormProps {
  video: Omit<Video, "id" | "createdAt" | "updatedAt">;
  languageCodes: LanguageCode[];
  profiles: Profile[];
  chats: Chat[];
  onVideoChange: (video: Omit<Video, "id" | "createdAt" | "updatedAt">) => void;
  errors?: Record<keyof Video, string>;
  children?: React.ReactNode;
}

export const VideoForm: React.FC<VideoFormProps> = ({ video, languageCodes, profiles, chats, onVideoChange, errors, children }) => {
  function handleVideoChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> | SelectChangeEvent<unknown>) {
    const field = event.target.name;

    if (["duration", "viewCount", "likeCount", "dislikeCount", "commentCount"].includes(field)) {
      if (parseInt(event.target.value as string) < 0) {
        event.target.value = 0;
      }
    }

    if (field === "chatId" && event.target.value === "0") {
      event.target.value = null;
    }

    if (field === "gane" && event.target.value === "") {
      event.target.value = null;
    }

    onVideoChange({ ...video, [field]: event.target.value });
  }

  function handleSwitchChange(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) {
    const field = event.target.name;
    onVideoChange({ ...video, [field]: checked });
  }

  return (
    <>
      <div className="post-panel" style={{ gap: "1rem" }}>
        <TextField error={!!errors?.name} helperText={errors?.name} className="spacer" variant="outlined" label="Name" name="name" value={video.name} onChange={handleVideoChange} />
        <TextField error={!!errors?.title} helperText={errors?.title} className="spacer" variant="outlined" label="Title" name="title" value={video.title} onChange={handleVideoChange} />
        <TextField error={!!errors?.description} helperText={errors?.title} className="spacer" style={{ width: "100%" }} multiline minRows={3} maxRows={10} label="Description" name="description" value={video.description} onChange={handleVideoChange} />
        <TextField error={!!errors?.thumbnailUrl} helperText={errors?.thumbnailUrl} className="spacer" variant="outlined" label="Thumbnail URL" name="thumbnailUrl" value={video.thumbnailUrl} onChange={handleVideoChange} />
        <TextField error={!!errors?.contentUrl} helperText={errors?.contentUrl} className="spacer" variant="outlined" label="Content URL" name="contentUrl" value={video.contentUrl} onChange={handleVideoChange} />
        <TextField inputProps={{ min: 0, type: "number" }} variant="outlined" label="Duration" name="duration" value={video.duration} onChange={handleVideoChange} />
        <TextField error={!!errors?.publishedAt} helperText={errors?.publishedAt} className="spacer" variant="outlined" label="Published At" name="publishedAt" value={video.publishedAt} onChange={handleVideoChange} />
        <TextField inputProps={{ min: 0, type: "number" }} variant="outlined" label="View Count" name="viewCount" value={video.viewCount} onChange={handleVideoChange} />
        <TextField inputProps={{ min: 0, type: "number" }} variant="outlined" label="Like Count" name="likeCount" value={video.likeCount} onChange={handleVideoChange} />
        <TextField inputProps={{ min: 0, type: "number" }} variant="outlined" label="Dislike Count" name="dislikeCount" value={video.dislikeCount} onChange={handleVideoChange} />
        <TextField inputProps={{ min: 0, type: "number" }} variant="outlined" label="Comment Count" name="commentCount" value={video.commentCount} onChange={handleVideoChange} />
        <FormControlLabel className="spacer"
          control={
            <Switch
              checked={video.muted}
              onChange={handleSwitchChange}
              name="muted"
              color="primary"
            />
          }
          label="Muted"
        />
        <TextField error={!!errors?.game} helperText={errors?.game} className="spacer" variant="outlined" label="Game" name="game" value={video.game ?? ""} onChange={handleVideoChange} />

        <FormControlLabel className="spacer"
          control={
            <Switch
              checked={video.viewabilityTracking}
              onChange={handleSwitchChange}
              name="viewabilityTracking"
              color="primary"
            />
          }
          label="Viewability Tracking"
        />

        <FormControlLabel className="spacer"
          control={
            <Switch
              checked={video.processed}
              onChange={handleSwitchChange}
              name="processed"
              color="primary"
            />
          }
          label="Processed"
        />
      </div>
      <div>Preview coming soon</div>
      <div className="unique-panel" style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <FormControl className="spacer" variant="outlined">
          <InputLabel>Chat</InputLabel>
          <Select
            value={video.chatId ?? 0}
            onChange={handleVideoChange}
            label="Chat"
            name="chatId"
          >
            <MenuItem value={0}>None</MenuItem>
            {chats.map((chat) => (
              <MenuItem value={chat.id} key={chat.id}>{chat.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl className="spacer" variant="outlined">
          <InputLabel variant="outlined" htmlFor="profileId">Profile</InputLabel>
          <NativeSelect
            value={video.profileId}
            onChange={handleVideoChange}
            inputProps={{
              name: 'age',
              id: 'profileId',
            }}
            name="profileId"
          >
            {profiles.map((profile) => (
              <option value={profile.id} key={profile.id}>{profile.name}</option >
            ))}
          </NativeSelect>
        </FormControl>
        <FormControl className="spacer" variant="outlined">
          <InputLabel>Language Code</InputLabel>
          <Select
            value={video.languageCodeId}
            onChange={handleVideoChange}
            label="Language Code"
            name="languageCodeId"
          >
            {languageCodes.map((languageCode) => (
              <MenuItem value={languageCode.id} key={languageCode.id}>{languageCode.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {children}
      </div>
    </>
  );
};
